import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

import { IMaskInput } from "react-imask";

import { Eye, EyeSlash } from "../../../../assets/svg";

import { IFormLogin, IOptionTypeUsername } from "../../interfaces";
import { LayoutAuth } from "../../../../components/LayoutAuth";
import { Switch } from "../../../../components/Switch";
import { IRequestSignIn } from "../../../../common/interfaces";

import { useAuth } from "../../../../hooks/useAuth";
import { useNotification } from "../../../../hooks/useNotification";
import { TypeUsername } from "../../enum";

import { useTranslate } from "../../../../hooks/useTranslate";
import {
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { SelectLanguage } from "../../../../components/SelectLanguage";
import countryCodes from "../../../../helpers/countryCodes";
import Flag from "react-flagkit";
import {
  AutocompleteElement,
  FormContainer,
  TextFieldElement,
} from "react-hook-form-mui";
import { isEmpty, uniqBy } from "lodash";

interface TextMaskFieldProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  mask: string;
  name: string;
}

const TextMaskField = React.forwardRef<HTMLElement, TextMaskFieldProps>(
  (props, ref) => {
    const { onChange, ...other } = props;
    return (
      <IMaskInput
        {...other}
        unmask
        definitions={{
          "#": /[1-9]/,
        }}
        inputRef={ref as any}
        onAccept={(value: any) => {
          onChange({
            target: {
              name: props.name,
              value: isEmpty(value) ? undefined : value,
            },
          });
        }}
        overwrite
      />
    );
  }
);

const Login = () => {
  const { errorNotification } = useNotification();
  const { signIn, loadUser } = useAuth();
  const { translate } = useTranslate();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signType, setSignType] = useState<TypeUsername>(TypeUsername.email);

  const schema = yup.object({
    ...(signType === TypeUsername.email
      ? {
          email: yup
            .string()
            .email(translate("global.email_invalid"))
            .required(translate("global.email_required")),
        }
      : {}),
    ...(signType === TypeUsername.phone
      ? {
          phone: yup.object().shape({
            code: yup
              .string()
              .nullable()
              .required(translate("global.phone_required")),
            number: yup
              .string()
              .transform((v) => (isEmpty(v) ? undefined : v))
              .nullable()
              .required(translate("global.phone_required")),
          }),
        }
      : {}),
    password: yup.string().required(translate("global.password_required")),
  });

  const form = useForm<IFormLogin>({
    reValidateMode: "onBlur",
    mode: "all",
    defaultValues: {
      phone: {
        code: "BR",
      },
    },
    resolver: yupResolver(schema),
  });

  const submitForm = async ({ email, phone, password }: IFormLogin) => {
    console.log(phone);

    setIsLoading(true);

    const phoneCode = countryCodes.find((c) => c.code === phone?.code);

    const userName = email ?? `+${phoneCode?.phone}${phone?.number}`;
    const body: IRequestSignIn = {
      password,
      userName,
    };

    try {
      await signIn(body);
      await loadUser();
      setIsLoading(false);
      navigate("/content-center");
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status === 401) {
        errorNotification(translate("auth.invalid_credentials"));
      } else if (err.response?.status === 400) {
        errorNotification(translate("auth.user_not_confirmed"));
      } else if (err.response?.status === 403 || err.message === "Forbidden") {
        errorNotification(translate("auth.user_forbidden"));
      }
      setIsLoading(false);
    }
  };

  const options: IOptionTypeUsername[] = [
    { value: TypeUsername.email, label: translate("global.email") },
    { value: TypeUsername.phone, label: translate("global.phone") },
  ];

  const selectSignType = (value: TypeUsername) => {
    form.unregister(value === TypeUsername.email ? "email" : "phone");
    setSignType(value);
  };

  const values = form.watch();

  const countryCodeFlag = useMemo(() => {
    const countryCode = values.phone?.code ?? {};

    if (!countryCode) return undefined;
    const { code } = countryCodes.find((c) => c.code === countryCode) ?? {};

    if (code) return <Flag country={code} size={20} />;
    return undefined;
  }, [values]);

  return (
    <LayoutAuth>
      <FormContainer formContext={form} onSuccess={submitForm}>
        <Stack direction="column" spacing={3}>
          <Stack direction="row" alignItems="center">
            <Typography
              color="primary"
              sx={{
                flexGrow: 1,
                fontSize: 25,
                fontWeight: "bold",
              }}
            >
              {translate("auth.title_sign_in")}
            </Typography>
            <SelectLanguage />
          </Stack>
          <Switch
            currentActive={signType}
            options={options}
            onChange={(value) => {
              selectSignType(value as TypeUsername);
            }}
          />

          <Stack direction="column" spacing={0}>
            <Collapse in={TypeUsername.email === signType} unmountOnExit>
              <TextFieldElement
                name="email"
                sx={{ mb: 1 }}
                fullWidth
                label={translate("global.email")}
                placeholder={translate("global.placeholder_email")}
                type="email"
              />
            </Collapse>
            <Collapse in={TypeUsername.phone === signType} unmountOnExit>
              <Stack direction="row">
                <AutocompleteElement
                  matchId
                  label={translate("global.phone_code")}
                  name="phone.code"
                  textFieldProps={{
                    InputProps: {
                      startAdornment: !!countryCodeFlag && countryCodeFlag,
                    },
                  }}
                  autocompleteProps={{
                    disableClearable: true,
                    sx: { minWidth: 1 / 3 },
                    renderOption: (props, option) => (
                      <Stack
                        height={23}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        component="li"
                        {...props}
                      >
                        <Flag country={option.data.code} size={20} />
                        <Typography component="span">{option.label}</Typography>
                      </Stack>
                    ),
                  }}
                  options={uniqBy(countryCodes, (c) => c.phone + c.code).map(
                    (c) => ({
                      label: ` ${c.code}`,
                      id: c.code,
                      data: c,
                    })
                  )}
                />
                <TextFieldElement
                  sx={{ mb: 1 }}
                  fullWidth
                  label={translate("global.phone")}
                  placeholder={translate("global.placeholder_phone")}
                  name="phone.number"
                  InputProps={{
                    inputProps: {
                      mask:
                        String(values.phone?.code ?? "").toLocaleUpperCase() ===
                        "BR"
                          ? "(##) #####-####"
                          : "###############",
                    } as any,
                    inputComponent: TextMaskField as any,
                  }}
                />
              </Stack>
            </Collapse>
            <TextFieldElement
              fullWidth
              name="password"
              label={translate("global.password")}
              placeholder={translate("global.placeholder_password")}
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setShowPassword((old) => !old);
                      }}
                    >
                      {showPassword ? <EyeSlash /> : <Eye />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Link
            underline="hover"
            sx={{ alignSelf: "flex-end" }}
            component={RouterLink}
            to="/recover-password"
          >
            {translate("auth.recover_password")}
          </Link>
          {/* {signType === TypeUsername.email ? (
          <Input
            error={errors.email?.message}
            placeholder={translate("global.placeholder_email")}
            type="email"
          />
        ) : (
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Phone
                placeholder={translate("global.placeholder_phone")}
                value={value}
                onChange={onChange}
                defaultCountry="BR"
                error={errors.phone?.message}
              />
            )}
          />
        )} */}
          {/* <Phone
                placeholder={translate("global.placeholder_phone")}
                value={value}
                onChange={onChange}
                defaultCountry="BR"
                error={errors.phone?.message}
              /> */}
          <Stack spacing={1}>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isLoading}
              sx={{ textTransform: "uppercase" }}
            >
              {!isLoading ? (
                translate("auth.btn_enter")
              ) : (
                <CircularProgress size={25} />
              )}
            </Button>

            <Button
              color="secondary"
              variant="outlined"
              component={RouterLink}
              type="button"
              to="confirm-invivation"
              sx={{ textTransform: "uppercase" }}
            >
              {translate("auth.btn_invite")}
            </Button>
          </Stack>

          {/* <FooterCard>
        <Google />
        <Facebook />
      </FooterCard> */}
          {/* <Link
            underline="hover"
            sx={{ alignSelf: "center" }}
            component={RouterLink}
            to="/signup"
          >
            {translate("auth.sign_up")}
          </Link> */}
          <Link
            component={RouterLink}
            underline="hover"
            sx={{ alignSelf: "center" }}
            to="/terms-of-service"
          >
            {translate("auth.terms_of_service")}
          </Link>
        </Stack>
      </FormContainer>
    </LayoutAuth>
  );
};

export { Login };
